/* eslint-disable import/no-extraneous-dependencies */
import { rest } from 'msw';
import { mockServerApi, findById } from '@/common/utils';
import { PostRequest } from '@/views/assets/interfaces';
import { ASSETS_LIBRARY_LIST, EMPTY_ASSETS_LIBRARY_LIST } from '../data';

/**
* split the query string into an array of strings, each one representing a query.
* map each query string into an object with the following properties:
  - field: the field name (e.g. 'name')
  - operator: the operator (e.g. '=')
  - value: the value (e.g. 'John')
* We return an array of objects.
*/
function getQueries(list) {
  const result = list.map((query) => {
    const q = query.split(':');
    return {
      field: q[0],
      operator: q[1],
      value: q[2],
    };
  });
  return result;
}

const videoMetadata = {
  avgFrameRate: '25/1',
  bitRate: 182960,
  codecName: 'h264',
  codecTagString: 'avc1',
  codecType: 'video',
  displayAspectRatio: 'N/A',
  duration: 29.52,
  durationTimeBase: 377856,
  frames: 738,
  height: 720,
  timeBase: '1/12800',
  width: 1280,
};

const NEW_ASSETS = [];

export const assetsRequests = (msw: typeof rest) => [
  msw.get(mockServerApi('/assets/library'), (req, res, ctx) => {
    const companiesId = req.url.searchParams.get('companies') || null;
    const parentId = req.url.searchParams.get('parentId') || null;
    const searchParams = req.url.searchParams.getAll('q');
    const queryList = getQueries(searchParams);

    // only return assets for company nike
    if (companiesId === '789') {
      return res(
        ctx.status(200),
        ctx.json({ items: ASSETS_LIBRARY_LIST.null.items, folders: [] }),
      );
    }

    // for any other company that is not craftsman+ return an empty list
    if (companiesId !== '123') {
      return res(
        ctx.status(200),
        ctx.json(EMPTY_ASSETS_LIBRARY_LIST.null),
      );
    }

    if (searchParams.length === 0) {
      return res(
        ctx.status(200),
        ctx.json(ASSETS_LIBRARY_LIST[parentId]),
      );
    }

    /**
    * Map the items in the list to a use type instead of mimeType.
    * filter the list to only include items that match the query.
    */
    const itemResult = ASSETS_LIBRARY_LIST[parentId].items
      .map((i) => ({ ...i, type: i.mimeType }))
      .filter((item) => {
        const operators = {
          '=': (a, b) => ((a) ? a.toLowerCase() === b.toLowerCase() : null),
          contains(a, b) { return (a) ? a.toLowerCase().includes(b.toLowerCase()) : null; },
        };

        return queryList.map((query) => {
          /* skip if empty */
          if (!query.field) return true;
          const { field, operator, value } = query;
          return operators[operator](item[field], value);
        }).every(Boolean);
      });

    const folderResult = ASSETS_LIBRARY_LIST[parentId].folders;

    return res(
      ctx.status(200),
      ctx.delay(100),
      ctx.json({ items: itemResult, folders: folderResult }),
    );
  }),

  msw.post(mockServerApi('/assets'), (req:{ body: PostRequest }, res, ctx) => {
    const { mimeType, name } = req.body;
    const type = mimeType.split('/')[0];

    let sourceUrl;
    let assetName;
    let metadata;

    const assetId = (Math.random() + 1).toString(36).substring(7);

    if (type === 'video') {
      sourceUrl = `assets/sample_640x360-1-${assetId}.mp4`;
      assetName = 'sample_640x360.mp4';
      metadata = videoMetadata;
    } else {
      sourceUrl = `assets/icons8-jake-500-1-${assetId}.png`;
      assetName = 'jake-500';
      metadata = { height: 500, width: 500 };
    }

    const asset = {
      folderId: null,
      id: assetId,
      isDirectory: false,
      isFile: true,
      mimeType,
      metadata,
      name: assetName,
      size: '5416',
      sizeBase64: '3315811',
      src: sourceUrl,
      createdAt: new Date(Date.now()).getTime(),
      updatedAt: new Date(Date.now()).getTime(),
    };

    NEW_ASSETS.push(asset);
    ASSETS_LIBRARY_LIST[asset.folderId].items.push(asset);

    return res(
      ctx.status(200),
      ctx.json({
        expires: 3600,
        id: asset.id,
        mimeType,
        name,
        src: sourceUrl,
        url: `${process.env.VUE_APP_API_URI}/preSigned`,
      }),
    );
  }),

  msw.get(mockServerApi('/assets/:id'), (req, res, ctx) => {
    const { id } = req.params;
    const data = findById(NEW_ASSETS, id.toString());
    return res(ctx.status(200), ctx.json(data));
  }),

  msw.put(mockServerApi('/preSigned'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({}),
  )),

  msw.patch(mockServerApi('/assets/:id'), (req: { body: { name: string } }, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      ...req.body,
      updatedAt: new Date(Date.now()).getTime(),
    }),
  )),

  msw.delete(mockServerApi('/assets/:id'), (req, res, ctx) => res(
    ctx.delay(100),
    ctx.status(200),
    ctx.json({}),
  )),

  msw.post(mockServerApi('/assets/folders'), (req: { body: { name: string } }, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      ...req.body,
      id: (Math.random() + 1).toString(36).substring(7),
      createdAt: new Date(Date.now()).getTime(),
      updatedAt: new Date(Date.now()).getTime(),
      coverImageS3Url: null,
      userId: null,
    }),
  )),

  msw.patch(mockServerApi('/assets/folders/:id'), (req: { body: { name: string } }, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      ...req.body,
      updatedAt: new Date(Date.now()).getTime(),
    }),
  )),

  msw.delete(mockServerApi('/assets/folders/:id'), (req: { params: { id: string } }, res, ctx) => {
    if (req.params.id === 'aysdFcsJ4aT') {
      return res(
        ctx.status(500),
        ctx.json({
          message: 'Cannot delete folder with assets',
        }),
      );
    }

    return res(
      ctx.status(200),
      ctx.json({}),
    );
  }),

  msw.get(mockServerApi('/assets/:id/progress'), (req, res, ctx) => {
    const OPTIONS = [
      { status: 'QUEUED', progress: 25 },
      { status: 'PROGRESS', progress: 75 },
      { status: 'DONE', progress: 0 },
    ];

    const randomResult = OPTIONS[Math.floor(Math.random() * (OPTIONS.length))];

    return res(
      ctx.status(200),
      ctx.json({
        progress: randomResult.progress,
        stages: [{ name: 'Optimize', progress: randomResult.progress }],
        status: randomResult.status,
      }),
      ctx.delay(500),
    );
  }),
];

export default assetsRequests;
